import { useCallback, useMemo, useRef, useState } from 'react';
import type { ColumnSchema, DuckDbType } from '@/types/api';
import type { DistinctValue } from '@/cache/ResultCache';
import type { GridMode } from '@/cache/types';
import { ColumnPicker } from './ColumnPicker';
import { ValueEditor } from './ValueEditor';
import {
  arityOf,
  coerceOperator,
  isRowApplicable,
  moveSortRow,
  newFilterRow,
  newSortRow,
  operatorMeta,
  operatorsFor,
  parsePredicate,
  serializePredicate,
  takesNoValue,
  toFilterSpecs,
  toSortSpecs,
  type FilterRow,
  type FilterSortState,
  type PanelMode,
} from './filterModel';

export interface AppliedFilterSort {
  readonly filters: ReturnType<typeof toFilterSpecs>;
  readonly sort: ReturnType<typeof toSortSpecs>;
  readonly predicate: string | null;
}

export interface FilterSortPanelProps {
  readonly columns: readonly ColumnSchema[];
  readonly state: FilterSortState;
  readonly mode: GridMode;
  readonly onChange: (state: FilterSortState) => void;
  readonly onApply: (applied: AppliedFilterSort) => void;
  readonly onClose: () => void;
  readonly loadDistinct: (column: string) => Promise<readonly DistinctValue[] | null>;
}

export function FilterSortPanel({
  columns,
  state,
  mode,
  onChange,
  onApply,
  onClose,
  loadDistinct,
}: FilterSortPanelProps): JSX.Element {
  const [editing, setEditing] = useState<string | null>(null);

  const types = useMemo(
    () => new Map<string, DuckDbType>(columns.map((column) => [column.name, column.type])),
    [columns],
  );

  const typeOf = (name: string): DuckDbType | undefined => types.get(name);

  // Text mode is only representable in the builder if it parses.
  const parsed = useMemo(
    () => (state.mode === 'text' ? parsePredicate(state.text) : null),
    [state.mode, state.text],
  );
  const builderBlocked = state.mode === 'text' && parsed?.rows === null;

  const patch = (changes: Partial<FilterSortState>): void => onChange({ ...state, ...changes });

  const setRows = (rows: readonly FilterRow[]): void => patch({ rows });

  const updateRow = (id: string, changes: Partial<FilterRow>): void => {
    setRows(state.rows.map((row) => (row.id === id ? { ...row, ...changes } : row)));
  };

  const switchMode = (next: PanelMode): void => {
    if (next === state.mode) return;
    if (next === 'text') {
      // Seed the textarea from the builder so nothing is lost on the way in.
      patch({ mode: 'text', text: serializePredicate(state.rows, types) });
      return;
    }
    const result = parsePredicate(state.text);
    if (result.rows === null) return; // Builder tab is disabled in this case.
    patch({ mode: 'builder', rows: result.rows });
  };

  const apply = useCallback(() => {
    if (state.mode === 'text') {
      const raw = state.text.trim();
      onApply({ filters: [], sort: toSortSpecs(state.sort), predicate: raw === '' ? null : raw });
      return;
    }
    onApply({
      filters: toFilterSpecs(state.rows),
      sort: toSortSpecs(state.sort),
      predicate: null,
    });
  }, [state, onApply]);

  const activeCount = state.rows.filter(isRowApplicable).length;

  return (
    <section
      className="flex flex-col border-b border-line bg-surface"
      aria-label="Filter and sort"
      data-testid="filter-sort-panel"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          apply();
        }
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-line px-2.5 py-1.5">
        <span className="eyebrow">Filter</span>
        {activeCount > 0 && state.mode === 'builder' ? (
          <span className="tnum text-[10px] text-faint">{activeCount} active</span>
        ) : null}

        <div className="ml-auto flex items-center gap-2" role="radiogroup" aria-label="Editor mode">
          <label className="flex cursor-pointer items-center gap-1 text-[11px]">
            <input
              type="radio"
              name="filter-mode"
              checked={state.mode === 'builder'}
              disabled={builderBlocked}
              onChange={() => switchMode('builder')}
            />
            <span
              className={builderBlocked ? 'text-faint' : 'text-ink'}
              title={
                builderBlocked
                  ? `${parsed?.reason ?? 'This expression is too complex to represent.'} Editing continues in Text.`
                  : undefined
              }
            >
              Builder
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-1 text-[11px]">
            <input
              type="radio"
              name="filter-mode"
              checked={state.mode === 'text'}
              onChange={() => switchMode('text')}
            />
            <span className="text-ink">Text</span>
          </label>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close filter panel">
            ✕
          </button>
        </div>
      </div>

      {builderBlocked ? (
        <p className="border-b border-line bg-remote-dim px-2.5 py-1 text-[10px] text-remote">
          {parsed?.reason} Your expression is kept exactly as written.
        </p>
      ) : null}

      {/* Body: filter rows and sort share one scroll region so neither is
          cramped when the other grows. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-auto" style={{ maxHeight: '48vh' }}>
      <div className="px-2.5 py-1.5">
        {state.mode === 'text' ? (
          <textarea
            className="w-full resize-y rounded-sm border border-line bg-base px-2 py-1.5 font-mono text-xs text-ink outline-none focus:border-local"
            rows={4}
            aria-label="Filter expression"
            placeholder={`"region" = 'Riyadh' AND "age" >= 40`}
            value={state.text}
            onChange={(event) => patch({ text: event.target.value })}
          />
        ) : state.rows.length === 0 ? (
          <p className="py-1 text-[11px] text-muted">
            No conditions.{' '}
            <button
              type="button"
              className="text-local underline"
              onClick={() =>
                setRows([newFilterRow(columns[0]?.name ?? '', columns[0]?.type)])
              }
            >
              Add one
            </button>
            .
          </p>
        ) : (
          <div className="space-y-1">
            {state.rows.map((row, index) => (
              <FilterRowEditor
                key={row.id}
                row={row}
                index={index}
                isLast={index === state.rows.length - 1}
                columns={columns}
                type={typeOf(row.column)}
                editing={editing === row.id}
                onEdit={(open) => setEditing(open ? row.id : null)}
                loadDistinct={loadDistinct}
                onChange={(changes) => updateRow(row.id, changes)}
                onRemove={() => setRows(state.rows.filter((entry) => entry.id !== row.id))}
                onAdd={() => {
                  const next = [...state.rows];
                  next.splice(
                    index + 1,
                    0,
                    newFilterRow(columns[0]?.name ?? '', columns[0]?.type),
                  );
                  setRows(next);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sort */}
      <div className="border-t border-line px-2.5 py-1.5">
        <div className="flex items-center gap-2">
          <span className="eyebrow">Sort by</span>
          <button
            type="button"
            className="icon-btn h-5"
            aria-label="Add sort column"
            onClick={() => patch({ sort: [...state.sort, newSortRow(columns[0]?.name ?? '')] })}
          >
            +
          </button>
        </div>

        {state.sort.length === 0 ? (
          <p className="py-0.5 text-[11px] text-muted">Unsorted.</p>
        ) : (
          <div className="mt-1 space-y-1">
            {state.sort.map((row, index) => (
              <div key={row.id} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  aria-label={`Enable sort on ${row.column}`}
                  onChange={(event) =>
                    patch({
                      sort: state.sort.map((entry) =>
                        entry.id === row.id ? { ...entry, enabled: event.target.checked } : entry,
                      ),
                    })
                  }
                />
                <span className="tnum w-4 shrink-0 text-[10px] text-faint">{index + 1}</span>
                <ColumnPicker
                  columns={columns}
                  value={row.column}
                  ariaLabel={`Sort column ${index + 1}`}
                  onChange={(column) =>
                    patch({
                      sort: state.sort.map((entry) =>
                        entry.id === row.id ? { ...entry, column } : entry,
                      ),
                    })
                  }
                />
                <select
                  className="field w-20"
                  aria-label={`Sort direction ${index + 1}`}
                  value={row.direction}
                  onChange={(event) =>
                    patch({
                      sort: state.sort.map((entry) =>
                        entry.id === row.id
                          ? { ...entry, direction: event.target.value as 'asc' | 'desc' }
                          : entry,
                      ),
                    })
                  }
                >
                  <option value="asc">asc</option>
                  <option value="desc">desc</option>
                </select>
                <button
                  type="button"
                  className="icon-btn h-5"
                  aria-label={`Move ${row.column} earlier`}
                  disabled={index === 0}
                  onClick={() => patch({ sort: moveSortRow(state.sort, row.id, -1) })}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="icon-btn h-5"
                  aria-label={`Move ${row.column} later`}
                  disabled={index === state.sort.length - 1}
                  onClick={() => patch({ sort: moveSortRow(state.sort, row.id, 1) })}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="icon-btn h-5 hover:!text-danger"
                  aria-label={`Remove sort on ${row.column}`}
                  onClick={() =>
                    patch({ sort: state.sort.filter((entry) => entry.id !== row.id) })
                  }
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      </div>

      {/* Apply */}
      <div className="flex items-center gap-2 border-t border-line px-2.5 py-1.5">
        {mode !== 'local' ? (
          <span className="text-[10px] text-remote">
            No local cache — filtering and sorting run on the server.
          </span>
        ) : (
          <span className="text-[10px] text-local">
            Runs in this tab against the cached rows. No network.
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            className="btn"
            onClick={() => {
              onChange({ mode: 'builder', rows: [], text: '', sort: [] });
              onApply({ filters: [], sort: [], predicate: null });
            }}
          >
            Clear
          </button>
          <button type="button" className="btn btn-run" onClick={apply}>
            Apply Filter &amp; Sort
          </button>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- row ----- */

interface FilterRowEditorProps {
  readonly row: FilterRow;
  readonly index: number;
  readonly isLast: boolean;
  readonly columns: readonly ColumnSchema[];
  readonly type: DuckDbType | undefined;
  readonly editing: boolean;
  readonly onEdit: (open: boolean) => void;
  readonly loadDistinct: (column: string) => Promise<readonly DistinctValue[] | null>;
  readonly onChange: (changes: Partial<FilterRow>) => void;
  readonly onRemove: () => void;
  readonly onAdd: () => void;
}

function FilterRowEditor({
  row,
  index,
  isLast,
  columns,
  type,
  editing,
  onEdit,
  loadDistinct,
  onChange,
  onRemove,
  onAdd,
}: FilterRowEditorProps): JSX.Element {
  const valueAnchorRef = useRef<HTMLButtonElement>(null);
  const operators = operatorsFor(type);
  const arity = arityOf(row.operator);
  const hidesValue = takesNoValue(row.operator);

  const summary = (() => {
    if (hidesValue) return '';
    if (arity === 'list') {
      const values = row.values.filter((value) => value !== '');
      return values.length === 0 ? '' : values.length === 1 ? (values[0] ?? '') : `${values.length} values`;
    }
    if (arity === 2) {
      return row.values[0] || row.values[1] ? `${row.values[0] ?? ''} … ${row.values[1] ?? ''}` : '';
    }
    return row.values[0] ?? '';
  })();

  return (
    <div className="flex items-center gap-1.5" data-testid={`filter-row-${index}`}>
      <input
        type="checkbox"
        checked={row.enabled}
        aria-label={`Enable condition ${index + 1}`}
        onChange={(event) => onChange({ enabled: event.target.checked })}
      />

      <ColumnPicker
        columns={columns}
        value={row.column}
        ariaLabel={`Condition ${index + 1} column`}
        onChange={(column) =>
          onChange({
            column,
            // Keep the operator if the new type still allows it.
            operator: coerceOperator(
              row.operator,
              columns.find((entry) => entry.name === column)?.type,
            ),
          })
        }
      />

      <select
        className="field w-24"
        aria-label={`Condition ${index + 1} operator`}
        value={row.operator}
        onChange={(event) =>
          onChange({ operator: event.target.value as FilterRow['operator'], values: [] })
        }
      >
        {operators.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {entry.label}
          </option>
        ))}
      </select>

      {hidesValue ? (
        // Hidden entirely, not disabled: `is null` has no operand at all.
        <span className="flex-1 text-[11px] text-faint">—</span>
      ) : arity === 2 ? (
        <>
          <input
            className="field flex-1"
            aria-label={`Condition ${index + 1} lower bound`}
            value={row.values[0] ?? ''}
            onChange={(event) =>
              onChange({ values: [event.target.value, row.values[1] ?? ''] })
            }
          />
          <span className="text-[10px] text-faint">and</span>
          <input
            className="field flex-1"
            aria-label={`Condition ${index + 1} upper bound`}
            value={row.values[1] ?? ''}
            onChange={(event) =>
              onChange({ values: [row.values[0] ?? '', event.target.value] })
            }
          />
        </>
      ) : (
        <div className="flex-1">
          <button
            ref={valueAnchorRef}
            type="button"
            className="field text-left font-mono text-[11px]"
            aria-label={`Condition ${index + 1} value`}
            aria-haspopup="dialog"
            aria-expanded={editing}
            onClick={() => onEdit(!editing)}
          >
            <span className={summary === '' ? 'text-faint' : ''}>{summary || '<?>'}</span>
          </button>
          {editing ? (
            <ValueEditor
              anchorRef={valueAnchorRef}
              column={row.column}
              multiple={arity === 'list'}
              initial={row.values}
              loadSuggestions={() => loadDistinct(row.column)}
              onCommit={(values) => {
                onChange({ values });
                onEdit(false);
              }}
              onCancel={() => onEdit(false)}
            />
          ) : null}
        </div>
      )}

      {isLast ? (
        <span className="w-8" />
      ) : (
        <select
          className="field w-14"
          aria-label={`Join condition ${index + 1} to the next with`}
          value={row.joiner}
          onChange={(event) => onChange({ joiner: event.target.value as 'and' | 'or' })}
        >
          <option value="and">and</option>
          <option value="or">or</option>
        </select>
      )}

      <button type="button" className="icon-btn h-5" onClick={onAdd} aria-label={`Add condition after ${index + 1}`}>
        +
      </button>
      <button
        type="button"
        className="icon-btn h-5 hover:!text-danger"
        onClick={onRemove}
        aria-label={`Remove condition ${index + 1}`}
      >
        ✕
      </button>
    </div>
  );
}

export { operatorMeta };
